import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { frameDistance } from '../lib/previewFraming.js'

/**
 * Lightweight static 3D preview of a character rig. Used by the character
 * creation wizard to show what a freshly-uploaded GLB looks like before
 * the character is committed.
 *
 * Materials: the preview renders whatever materials the GLB ships with.
 * Post-creation, the server's glb_import pass extracts every glTF material
 * into a real material_assets row that the runtime then drives via
 * material_mapping (see AvatarModel.jsx + lib/avatarMaterial.js).
 *
 * @param {string} rigBlobUrl - URL from URL.createObjectURL(file)
 * @param {function} onLoaded - Called when the rig finishes loading
 * @param {function} onError - Called if loading fails
 */
export function AvatarPreviewModel({ rigBlobUrl, onLoaded, onError }) {
  const { camera, scene, gl, size } = useThree()
  const groupRef = useRef(null)
  const controlsRef = useRef(null)
  const [, setLoading] = useState(true)
  // The subject to frame, held until a MEASURED viewport exists. Framing used to
  // run inline at load time against whatever camera.aspect happened to be, and
  // an unmeasured canvas reports 0 — which sent the camera to Infinity (see
  // lib/previewFraming.js). The rig loads behind an await, so whether the canvas
  // was measured first was a race the user experienced as the camera flying off.
  //
  // STATE, not a ref, and that is the load-bearing part: the two events that
  // make framing possible — the rig finishing its load and the canvas being
  // measured — arrive in either order. A ref records the subject without
  // scheduling anything, so in the common ordering (canvas measured first, rig
  // loads second) `size` never changes again and the framing effect never
  // re-runs. Measured: that leaves the camera at its initial [0,0,3] and the
  // character unframed — trading one silent failure for another.
  const [subject, setSubject] = useState(null)
  const framedRef = useRef(false)

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = new THREE.Group()
      scene.add(groupRef.current)
    }

    if (!controlsRef.current) {
      controlsRef.current = new OrbitControls(camera, gl.domElement)
      controlsRef.current.autoRotate = false
      controlsRef.current.enableZoom = true
      controlsRef.current.enablePan = true
    }

    if (!rigBlobUrl) return
    setLoading(true)

    async function load() {
      try {
        const dracoLoader = new DRACOLoader().setDecoderPath('/draco/')
        const gltfLoader = new GLTFLoader().setDRACOLoader(dracoLoader)
        const gltf = await new Promise((resolve, reject) => {
          gltfLoader.load(rigBlobUrl, resolve, undefined, reject)
        })

        const rig = gltf.scene
        rig.name = '__preview__'
        rig.traverse((node) => {
          if (!node.isMesh) return
          node.castShadow = true
          node.receiveShadow = true
          if (node.isSkinnedMesh) node.frustumCulled = false
        })

        groupRef.current.clear()
        groupRef.current.add(rig)

        // Ground the rig so its lowest point sits on the grid (y=0). The main
        // runtime does this via AvatarModel's floor-snap (rigMinY / applyFloor);
        // without the same here, a GLB whose feet aren't authored at the origin
        // floats above the grid in the preview. Snap first, then frame.
        const groundBox = new THREE.Box3().setFromObject(rig)
        if (!groundBox.isEmpty()) rig.position.y -= groundBox.min.y

        // Record what to frame; the framing effect below applies it as soon as
        // the viewport has a real size. Deliberately NOT framed here.
        const bbox = new THREE.Box3().setFromObject(rig)
        if (!bbox.isEmpty()) {
          const sphere = new THREE.Sphere()
          bbox.getBoundingSphere(sphere)
          framedRef.current = false
          setSubject({ center: sphere.center.clone(), radius: sphere.radius })
        }

        setLoading(false)
        onLoaded?.()
      } catch (e) {
        console.error('[AvatarPreviewModel] Load failed:', e)
        onError?.(e)
        setLoading(false)
      }
    }

    load()
    return () => { /* Parent owns scene cleanup */ }
  }, [rigBlobUrl, camera, scene, gl])

  // Frame once the viewport is measured, and re-frame if it is measured LATER
  // (the race) or resized. Keyed on size so it re-runs on every measurement.
  useEffect(() => {
    if (!subject || framedRef.current || !controlsRef.current) return
    const res = frameDistance({
      radius: subject.radius,
      fovDeg: camera.fov,
      aspect: camera.aspect,
    })
    if (!res.ok) {
      // Not an error — the ordinary first pass before the canvas is measured.
      // Recorded on the global as well as logged, because "deferred" and
      // "never ran" are different bugs and a bare absent global cannot tell
      // them apart. That distinction cost a debugging round trip.
      if (typeof window !== 'undefined') {
        window.__PREVIEW_FRAMING__ = {
          framed: false, reason: res.reason,
          aspect: camera.aspect, fov: camera.fov, radius: subject.radius,
          size: { width: size.width, height: size.height },
        }
      }
      console.debug(`[AvatarPreviewModel] framing deferred: ${res.reason}`)
      return
    }
    camera.position.set(0, subject.center.y, res.dist)
    controlsRef.current.target.copy(subject.center)
    controlsRef.current.update()
    framedRef.current = true
    if (typeof window !== 'undefined') {
      // Repo convention (window.__* live-state globals) so the framing decision
      // is inspectable from the console instead of only from a screenshot.
      window.__PREVIEW_FRAMING__ = {
        framed: true,
        dist: res.dist,
        aspect: camera.aspect,
        fov: camera.fov,
        radius: subject.radius,
        size: { width: size.width, height: size.height },
        cameraPos: camera.position.toArray(),
      }
    }
  }, [subject, size, camera])

  useFrame(() => {
    if (controlsRef.current) controlsRef.current.update()
  })

  return null
}
